import React from 'react';
import { Container, SpaceBetween, Header, Spinner, Button } from "@amzn/awsui-components-react";
import { OrderSummaryTab } from "../../tabs/OrderSummaryTab";
import { ItemsRequestedTab, MessagesTab, InternalCommentsTab, RelationsTab } from "../../tabs";
import { OrderSubLineItemsTable } from "../../tabs/ItemsRequestedTab/SubLineItemsTable/OrderSubLineItemsTable";
import { GetCommentsCommandOutput } from "@amzn/ito-client";
import { getString } from "common";
import { UseMessagesPanelReturn } from "./useMessagesPanel";

/**
 * Interface representing configuration for string paths
 * @interface StringConfig
 * @property {string} stringPath - Base path for string resources
 * @property {string} messagesTitlePath - Path for messages title string
 * @property {string} notesTitlePath - Path for notes title string
 */
interface StringConfig {
  stringPath: string;
  messagesTitlePath: string;
  notesTitlePath: string;
}

/**
 * Interface representing order data and related comments/notes
 * @interface OrderData
 * @property {any} data - Order data
 * @property {any} comments - Order comments
 * @property {any} notes - Order notes
 */
interface OrderData {
  data: any;
  amznhamOrder?: any;
  comments: any;
  notes: any;
}

/**
 * Interface for state handler functions
 * @interface StateHandlers
 * @property {Function} setComments - Function to set comments state
 * @property {Function} setNotes - Function to set notes state
 */
interface StateHandlers {
  setComments: any;
  setNotes: any;
}

/**
 * Interface for display configuration options
 * @interface DisplayOptions
 * @property {boolean} showComments - Whether to show comments section
 * @property {boolean} showNotes - Whether to show notes section
 * @property {boolean} isAmznham - Whether user is Amznham
 * @property {boolean} [showAllSubLineItems] - Whether to show all sub line items
 */
interface DisplayOptions {
  showComments: boolean;
  showNotes: boolean;
  isAmznham: boolean;
  isItseOrder?: boolean;
  canComment?: boolean;
  showAllSubLineItems?: boolean;
}

/**
 * Interface for props used by SinglePageSections hook
 * @interface UseSinglePageSectionsProps
 * @property {StringConfig} stringConfig - Configuration for string resources
 * @property {OrderData} orderData - Order data and related info
 * @property {StateHandlers} stateHandlers - State management functions
 * @property {DisplayOptions} displayOptions - Display configuration options
 * @property {any} props - Additional props
 * @property {any} attachmentHook - Hook for attachment functionality
 * @property {Function} [onCommentsRefresh] - Optional function to refresh comments
 * @property {number} [refreshTrigger] - Optional trigger to refresh comments
 */
interface UseSinglePageSectionsProps {
  stringConfig: StringConfig;
  orderData: OrderData;
  stateHandlers: StateHandlers;
  displayOptions: DisplayOptions;
  props: any;
  attachmentHook: any;
  commentsSource: string;
  onCommentsRefresh?: () => void;
  refreshTrigger?: number;
  messagesPanel?: UseMessagesPanelReturn;
}

export const useSinglePageSections = ({
  stringConfig: { stringPath, messagesTitlePath, notesTitlePath },
  orderData: { data, amznhamOrder, comments, notes },
  stateHandlers: { setComments, setNotes },
  displayOptions: { showComments, showNotes, isAmznham, isItseOrder, canComment, showAllSubLineItems = false },
  props,
  attachmentHook,
  onCommentsRefresh,
  refreshTrigger,
  commentsSource,
  messagesPanel
}: UseSinglePageSectionsProps) => {

  const renderOrderSummarySection = () => (
    <Container>
      <OrderSummaryTab
        order={data!}
        amznhamOrder={amznhamOrder}
        useOrderEditItems={props.useOrderEditItems}
        useOrderEditActions={props.useOrderEditActions}
        flashBarItemsActions={props.useFlashBarItemsActions!}
        attachmentsHook={attachmentHook}
        hideRequesterDetails={true}
      />
    </Container>
  );

  const renderItemsRequestedSection = () => (
    <ItemsRequestedTab
      data-testid="items-requested-tab"
      order={data!}
      amznhamOrder={amznhamOrder}
      items={data?.items!}
      selectedItems={props.useOrderEditItems.selectedItems}
      useOrderEditItems={props.useOrderEditItems}
      useOrderActions={props.useOrderEditActions}
      flashBarItemsActions={props.useFlashBarItemsActions!}
      onCommentsRefresh={onCommentsRefresh}
    />
  );

  const renderItemsTable = (items: any[]) => {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <SpaceBetween data-testid="items-requested-tab" direction="vertical" size="m">
        <br />
        <Header
          data-testid="items-requested-section-header"
          variant="awsui-h1-sticky"
          description={getString("sublineItemsHeaderDescription", stringPath)}
        >
          {getString('orderDetails.tabs.itemsRequested.table.SublineItemsTableHeader')}
        </Header>
        {items.map((item: any) => (
          <OrderSubLineItemsTable
            key={item.itemId}
            useOrderEditItems={{
              ...props.useOrderEditItems,
              lastLineItemSelected: item,
              orderSelected: { orderId: data?.orderId },
              currentOrder: data
            }}
            useOrderEditActions={props.useOrderEditActions}
            reloadOrder={props.useOrderEditActions.reloadOrder}
            flashBarItemsActions={props.useFlashBarItemsActions!}
            itemName={item.itemInformation?.name || `Item ${item.itemId}`}
          />
        ))}
      </SpaceBetween>
    );
  };

  const renderSubLineItemsSection = () => {
    if (showAllSubLineItems) {
      const itemsSubLines = data?.items;
      return renderItemsTable(itemsSubLines);
    } else {
      return renderItemsTable(props.useOrderEditItems.selectedItems);
    }
  };

  const renderRelationsSection = () => {
    if (!data?.relations) {
      return null;
    }

    return (
      <Container>
        <RelationsTab
          order={data!}
        />
      </Container>
    );
  };

  const messagesPanelStringPath = `${stringPath}.messages`;

  const renderMessagesSectionHeaderActions = (panelName: 'messages' | 'notes') => {
    if (!messagesPanel || !messagesPanel.collapsibleMessagesEnabled || messagesPanel.isMinimized) return undefined;

    return (
      <SpaceBetween direction="horizontal" size="xs">
        {messagesPanel.isMaximized ? (
          <Button
            iconName="shrink"
            variant="icon"
            onClick={messagesPanel.closeMaximized}
            ariaLabel={getString("restore", messagesPanelStringPath)}
            data-testid={`${panelName}-restore-button`}
          />
        ) : (
          <>
            <Button
              iconName="expand"
              variant="icon"
              onClick={() => messagesPanel.maximize(panelName)}
              ariaLabel={getString("maximize", messagesPanelStringPath)}
              data-testid={`${panelName}-maximize-button`}
            />
            <Button
              iconName="close"
              variant="icon"
              onClick={messagesPanel.minimize}
              ariaLabel={getString("minimize", messagesPanelStringPath)}
              data-testid={`${panelName}-minimize-button`}
            />
          </>
        )}
      </SpaceBetween>
    );
  };

  const renderMessagesSection = () => (
    <Container>
      <div className={`single-page-container__scrollable-content${!showNotes ? ' single-page-container__scrollable-content--full-height' : ''}`}>
        {showComments ? (
          <SpaceBetween data-testid="messages-tab" direction="vertical" size="m">
            <Header variant="h2" data-testid="messages-section-header" actions={renderMessagesSectionHeaderActions('messages')}>
              {getString(messagesTitlePath, stringPath)}
            </Header>
            <MessagesTab
              order={data!}
              useOrderEditItems={props.useOrderEditItems}
              useOrderEditActions={props.useOrderEditActions}
              flashBarItemsActions={props.useFlashBarItemsActions!}
              comments={comments as GetCommentsCommandOutput}
              setDefaultComments={setComments}
              inputComment={props.useOrderEditItems.inputComment}
              setInputComment={props.useOrderEditActions.setInputComment}
              commentsSource={commentsSource}
              isAmznham={isAmznham}
              isItseOrder={isItseOrder}
              canComment={canComment}
              refreshTrigger={refreshTrigger}
            />
          </SpaceBetween>
        ) : (
          <div className="single-page-container__loading-container">
            <SpaceBetween direction="horizontal" size="m">
              <Header variant="h2" data-testid="loading-messages">
                {getString(`${stringPath}.loadingComments`)}
              </Header>
              <Spinner size="large" />
            </SpaceBetween>
          </div>
        )}
      </div>
    </Container>
  );

  const renderNotesSection = () => {
    if (!showNotes) {
      return null;
    }

    return (
      <Container>
        <div className="single-page-container__scrollable-content">
          <SpaceBetween data-testid="notes-tab" direction="vertical" size="m">
            <Header variant="h2" data-testid="notes-section-header" actions={renderMessagesSectionHeaderActions('notes')}>
              {getString(notesTitlePath, stringPath)}
            </Header>
            <InternalCommentsTab
              order={data!}
              useOrderEditItems={props.useOrderEditItems}
              useOrderEditActions={props.useOrderEditActions}
              flashBarItemsActions={props.useFlashBarItemsActions!}
              notes={notes}
              setDefaultNotes={setNotes}
              inputNotes={props.useOrderEditItems.inputNote}
              setInputNotes={props.useOrderEditActions.setInputNote}
            />
          </SpaceBetween>
        </div>
      </Container>
    );
  };

  return {
    renderOrderSummarySection,
    renderItemsRequestedSection,
    renderSubLineItemsSection,
    renderRelationsSection,
    renderMessagesSection,
    renderNotesSection
  };
};
